'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AskGregSection,
  ContactSupportSection,
  GeoRadiusProvider,
  ListStoreInstantSearch as ListStoreAIS,
  MapView,
  MapWrapper,
} from '@workspace/widgets';
import { AlgoliaMenuSelect, useGeoRadius } from '@workspace/widgets';
import { Button, PopupInformation, Spinner, Typography } from '@workspace/ui/common';
import { SYD_TOWN_HALL } from '@workspace/constants';
import { StoreLocatorTabs } from '@workspace/constants/tabs.constant';
import { StoreLocatorWithAskGreg } from '@workspace/interfaces/store-locator';
import { SingleStoreType } from '@workspace/interfaces/store-locator';
import { ALGOLIA_CONFIG, getTextAlignment } from '@/app/lib';
import { HeadingType, processHeading } from '@/app/lib/heading-utils';
import { cn } from '@/app/lib/utils';
import { InstantSearchProvider } from '@/app/providers';
import { MapProvider } from '@/app/providers/map-provider';
import { PlacesSearchBox } from '@workspace/widgets';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { Configure } from 'react-instantsearch';

// Helper to format geocoded address
const formatGeocodedAddress = (result: google.maps.GeocoderResult): string => {
  const components = result.address_components || [];
  let locality = '';
  let city = '';
  let country = '';

  for (const component of components) {
    if (component.types.includes('locality')) {
      locality = component.long_name || '';
    }
    if (
      component.types.includes('administrative_area_level_1') ||
      component.types.includes('administrative_area_level_2')
    ) {
      if (!city) city = component.long_name || '';
    }
    if (component.types.includes('country')) {
      country = component.long_name || '';
    }
  }

  const mainLocation = locality || city;
  if (mainLocation && country) return `${mainLocation}, ${country}`;
  if (mainLocation) return mainLocation;
  if (country) return country;

  // Fallback to formatted address
  const parts = result.formatted_address.split(',');
  return parts.length > 1
    ? `${parts[parts.length - 2]?.trim()}, ${parts[parts.length - 1]?.trim()}`
    : result.formatted_address;
};

const ERROR_LOCATION_CONTENT =
  'Your location can only be shown when location services are enabled and you are connected to the internet.';

function PageBody({ initialData: storeLocatorData }: { initialData?: StoreLocatorWithAskGreg }) {
  const [isReady, setIsReady] = useState<boolean>(false);
  const directionLabel = useMemo(() => storeLocatorData?.getDirectionLabel, [storeLocatorData?.getDirectionLabel]);

  const [activeView, setActiveView] = useState<StoreLocatorTabs>(StoreLocatorTabs.LIST_VIEW);
  const [selectedStore, setSelectedStore] = useState<SingleStoreType | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const { setCenterCallback, configureProps } = useGeoRadius();

  const [placesInputValue, setPlacesInputValue] = useState('');
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [justAllowed, setJustAllowed] = useState<boolean>(false);

  const geocodeLib = useMapsLibrary('geocoding');

  useEffect(() => {
    if (!geocodeLib) return;
    const geocode = new geocodeLib.Geocoder();
    geocoderRef.current = geocode;

    return () => {
      geocoderRef.current = null;
    };
  }, [geocodeLib]);

  // Check geolocation permission
  const checkGeolocationPermission = useCallback(async (): Promise<boolean> => {
    if (!('geolocation' in navigator)) {
      setShowPopup(true);
      return false;
    }

    if ('permissions' in navigator) {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'geolocation' as PermissionName,
        });
        if (permissionStatus.state === 'denied') {
          setShowPopup(true);
          return false;
        }
      } catch {
        // Permissions API not supported, continue
      }
    }
    return true;
  }, []);

  // Request location permission on mount
  useEffect(() => {
    const requestLocationPermission = async () => {
      const hasPermission = await checkGeolocationPermission();
      if (!hasPermission) return;

      // Only prompt if permission state is 'prompt' (not yet decided)
      if ('permissions' in navigator) {
        try {
          const permissionStatus = await navigator.permissions.query({
            name: 'geolocation' as PermissionName,
          });

          if (permissionStatus.state === 'prompt') {
            navigator.geolocation.getCurrentPosition(
              () => {
                // Permission granted
                setJustAllowed(true);
              },
              (error) => {
                if (error.code === 1) {
                  setShowPopup(true);
                }
              },
            );
          }
        } catch {
          // Permissions API not supported
        }
      }
    };

    requestLocationPermission();
  }, [checkGeolocationPermission]);

  const reverseGeocode = useCallback((lat: number, lng: number) => {
    if (!geocoderRef.current) return;

    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        setPlacesInputValue(formatGeocodedAddress(results[0]));
      }
    });
  }, []);

  const askGeoLocationHandler = useCallback(async (): Promise<void> => {
    const hasPermission = await checkGeolocationPermission();
    if (!hasPermission) return;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const userLocation = { lat: latitude, lng: longitude };
        setCenterCallback(userLocation);
        setSelectedStore(null);
        setSelectedKey(null);

        reverseGeocode(latitude, longitude);
      },
      () => {
        setShowPopup(true);
      },
    );
  }, [checkGeolocationPermission, reverseGeocode, setCenterCallback]);

  // If just allowed location permission, treat as if user clicked the button
  useEffect(() => {
    if (justAllowed) {
      askGeoLocationHandler();
      setJustAllowed(false);
    }
  }, [justAllowed, askGeoLocationHandler]);

  const headlineText: string | undefined = storeLocatorData?.headline?.title
    ? processHeading(storeLocatorData.headline.title as unknown as HeadingType).text
    : undefined;

  const Pane = useMemo(() => {
    return {
      classes: (tab: StoreLocatorTabs) =>
        cn(
          activeView === tab ? 'opacity-100' : 'opacity-0 absolute lg:relative w-full max-lg:pointer-events-none',
          'lg:opacity-100',
        ),
      buttonClass: (tab: StoreLocatorTabs) =>
        cn(
          'flex-1 px-4 py-3 text-md font-normal transition-colors relative duration-300 border-b-4 !font-openSans',
          activeView === tab
            ? 'text-primary border-primary bg-white !font-semibold leading-6'
            : 'text-secondary-400 border-white hover:text-secondary-600 bg-white',
        ),
    };
  }, [activeView]);

  const clearInput = useCallback(() => {
    setCenterCallback(SYD_TOWN_HALL);
    setSelectedStore(null);
    setSelectedKey(null);
    setPlacesInputValue('');
  }, [setCenterCallback]);

  const handleInputOnCenterSelected = useCallback(
    (coords: google.maps.LatLngLiteral, formatted: string) => {
      if (!coords.lat || !coords.lng) {
        console.error('Invalid coordinates selected from input', coords);
        return;
      }
      setCenterCallback(coords);
      setSelectedStore(null);
      setSelectedKey(null);
      setPlacesInputValue(formatted);
    },
    [setCenterCallback],
  );

  const handleOnSelectStore = useCallback(
    (store: SingleStoreType) => {
      if (selectedStore?.objectID === store.objectID) {
        setSelectedStore(null);
        setSelectedKey(null);
      } else {
        setSelectedStore(store);
        const key = store.objectID || null;
        setSelectedKey(key);
      }
    },
    [selectedStore],
  );

  useEffect(() => {
    setIsReady(true);
  }, []);

  return (
    <>
      <div className="relative min-h-screen">
        {storeLocatorData?.headline && !!storeLocatorData?.headline.isEnabled && (
          <div
            className={cn(
              'px-md py-xl md:px-4xl md:py-lg min-h-60 md:min-h-63 flex items-center porta-container',
              getTextAlignment(storeLocatorData.headline?.headingAlignment, true),
            )}
          >
            <div className="locator-hero-banner flex flex-col gap-2">
              {headlineText && (
                <Typography className="max-w-243.25" variant="h1" fontWeight="700">
                  {headlineText}
                </Typography>
              )}
              {storeLocatorData?.headline?.shortSummary && (
                <div className="store-location-des">
                  <Typography variant="bodyMd" className="max-w-146.5 max-lg:text-sm">
                    {storeLocatorData?.headline?.shortSummary}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )}

        {!isReady ? (
          <div className="flex items-center justify-center py-24">
            <Spinner size="large" className="text-primary" />
          </div>
        ) : (
          <InstantSearchProvider indexName={ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.StoreLocator}>
            <Configure {...configureProps} />
            <div className="pb-4 pl-align-portal md:pr-0! md:max-w-full! store-locator-container md:py-lg md:grid md:grid-cols-[606px_calc(100%-606px-32px)] md:gap-lg md:h-[calc(100vh-5.3125rem)] md:shrink-0">
              <div id="left-section" className="flex flex-col relative md:h-full overflow-hidden pl-1 -ml-1 pt-1 -mt-1">
                <div className="relative lg:w-max px-md md:px-0">
                  <Button
                    variant="primary"
                    iconName="OnAction"
                    iconSize={20}
                    iconPosition="left"
                    onClick={askGeoLocationHandler}
                    className="py-2.5 px-6 md:py-3 font-semibold rounded-md w-full"
                  >
                    Use my current location
                  </Button>
                  <PopupInformation
                    content={ERROR_LOCATION_CONTENT}
                    className="absolute max-lg:top-[calc(100%+20px)] lg:left-[calc(100%+20px)] top-[-40%]"
                    onClose={() => setShowPopup(false)}
                    isOpen={showPopup}
                  />
                </div>

                <div className="flex flex-col md:flex-row gap-md md:gap-lg py-lg lg:py-xl px-md md:px-0">
                  <div className="w-full">
                    <Typography variant="bodyMd" className="mb-2 lg:mb-4 font-openSans font-semibold">
                      Enter Suburb or Postcode
                    </Typography>
                    <PlacesSearchBox
                      value={placesInputValue}
                      placeholder="Enter a suburb or postcode"
                      onCenterSelected={handleInputOnCenterSelected}
                      onValueChange={setPlacesInputValue}
                      fullWidth={true}
                    />
                  </div>

                  {storeLocatorData?.isIncludedProductRangeFilter && (
                    <div className="w-full pr-0 md:pr-5">
                      <Typography variant="bodyMd" className="mb-2 lg:mb-4 font-openSans font-semibold">
                        Filter by Product Range
                      </Typography>
                      <div className="flex flex-1 gap-4 justify-between">
                        <AlgoliaMenuSelect
                          menuProps={{ attribute: 'productRanges.name' }}
                          placeholder="Product"
                          className="pr-0"
                          fullWidth={true}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center pb-4 md:hidden">
                  <div className="flex w-full border-secondary-200">
                    <Button
                      onClick={() => setActiveView(StoreLocatorTabs.LIST_VIEW)}
                      className={Pane.buttonClass(StoreLocatorTabs.LIST_VIEW)}
                      overrideClasses
                      aria-pressed={activeView === StoreLocatorTabs.LIST_VIEW}
                    >
                      {storeLocatorData?.listViewLabel}
                    </Button>
                    <Button
                      onClick={() => setActiveView(StoreLocatorTabs.MAP_VIEW)}
                      className={Pane.buttonClass(StoreLocatorTabs.MAP_VIEW)}
                      overrideClasses
                      aria-pressed={activeView === StoreLocatorTabs.MAP_VIEW}
                    >
                      {storeLocatorData?.mapViewLabel}
                    </Button>
                  </div>
                </div>

                {!!storeLocatorData?.isEnabledStoreListing && (
                  <div
                    className={cn(
                      'flex flex-col gap-sm max-md:h-146.75 md:flex-1 md:min-h-0',
                      Pane.classes(StoreLocatorTabs.LIST_VIEW),
                    )}
                  >
                    <ListStoreAIS
                      directionLabel={directionLabel}
                      selectedKey={selectedKey}
                      onSelectStoreAction={handleOnSelectStore}
                      notfoundHeading={storeLocatorData.notFoundHeading}
                      notfoundMessage={storeLocatorData.notFoundText}
                      clearInput={clearInput}
                    />
                  </div>
                )}
              </div>

              {!!storeLocatorData?.isEnabledMap && (
                <div
                  id="right-section"
                  className={cn(
                    'max-md:w-screen h-146.75 max-md:mx-[calc(50%-50vw)] md:h-full lg:block',
                    Pane.classes(StoreLocatorTabs.MAP_VIEW),
                  )}
                >
                  <div className="h-full relative">
                    <MapWrapper>
                      <MapView
                        selectedStore={selectedStore}
                        onSelectStore={handleOnSelectStore}
                        directionLabel={directionLabel}
                      />
                    </MapWrapper>
                  </div>
                </div>
              )}
            </div>
          </InstantSearchProvider>
        )}
      </div>

      {storeLocatorData?.askGreg?.isEnabled ? (
        <AskGregSection
          title={storeLocatorData?.askGreg?.heading || undefined}
          description={storeLocatorData?.askGreg?.description || undefined}
          quickPromptsTitle={storeLocatorData?.askGreg?.subheading || undefined}
          quickPrompts={
            Array.isArray(storeLocatorData?.askGreg?.prompts)
              ? (storeLocatorData?.askGreg?.prompts || []).map((text: string) => ({ text }))
              : undefined
          }
          isShowButton={storeLocatorData?.askGreg?.isShowButton ?? undefined}
          backgroundColor={storeLocatorData?.askGreg?.backgroundColor || undefined}
        />
      ) : null}

      {storeLocatorData?.isIncludedContactSupport ? (
        <ContactSupportSection
          locator={storeLocatorData?._id || 'storeLocator'}
          isSkip={!storeLocatorData?.isIncludedContactSupport}
        />
      ) : null}
    </>
  );
}

export default function ClientStoreLocatorPage({ initialData }: { initialData?: StoreLocatorWithAskGreg }) {
  return (
    <MapProvider>
      <GeoRadiusProvider>
        <PageBody initialData={initialData} />
      </GeoRadiusProvider>
    </MapProvider>
  );
}
